import { useEffect, useState } from 'react';
import {
  getSelectedFlights,
  getSelectedTickets,
  clearAllHolidayData,
  saveComboSessionId,
  saveSearchParams,
  saveSelectedFlight,
} from 'utils/sessionStorageHelper';
import type { SelectedFlight, SelectedTicket } from '../types';

/**
 * Custom hook to manage holiday booking session storage
 * Handles clearing old data and saving new selections when combo API changes
 */
export const useHolidaySession = () => {
  const [selectedTicketsMap, setSelectedTicketsMap] = useState<
    Record<string, SelectedTicket>
  >({});
  const [selectedFlight, setSelectedFlight] = useState<SelectedFlight | null>(
    null,
  );
  const [flightRefreshKey, setFlightRefreshKey] = useState(0);

  // Load initial selections from session storage on mount
  useEffect(() => {
    const savedTickets = getSelectedTickets();
    const ticketsMap: Record<string, SelectedTicket> = {};
    savedTickets.forEach(ticket => {
      if (ticket.productCode) {
        ticketsMap[ticket.productCode] = ticket;
      }
    });
    setSelectedTicketsMap(ticketsMap);

    const savedFlights = getSelectedFlights();
    if (savedFlights.length > 0) {
      setSelectedFlight(savedFlights[0]);
    }
  }, []);

  /**
   * Clear all session data when starting new combo search
   * This ensures fresh data when user changes filter conditions
   */
  const clearSessionForNewSearch = () => {
    clearAllHolidayData();
    setSelectedTicketsMap({});
    setSelectedFlight(null);
  };

  /**
   * Save initial flight selections from combo API response
   * Uses cheapest trip and fare as defaults
   */
  const saveInitialFlights = (flightData: any, sessionId: string) => {
    if (!flightData?.listFlight || flightData.listFlight.length === 0) {
      return;
    }

    flightData.listFlight.forEach((route: any) => {
      if (!route?.listTrip || route.listTrip.length === 0) return;

      // Find cheapest trip
      const cheapestTrip = route.listTrip.reduce((cheapest: any, trip: any) => {
        const tripPrice = Math.min(
          ...(trip.listPricing?.map((p: any) => p.additionalAmount || 0) || [
            0,
          ]),
        );
        const cheapestPrice = Math.min(
          ...(cheapest.listPricing?.map(
            (p: any) => p.additionalAmount || 0,
          ) || [Infinity]),
        );
        return tripPrice < cheapestPrice ? trip : cheapest;
      });

      // Find cheapest fare from cheapest trip
      const cheapestFare = cheapestTrip.listPricing?.reduce(
        (cheapest: any, fare: any) => {
          return (fare.additionalAmount || 0) < (cheapest.additionalAmount || 0)
            ? fare
            : cheapest;
        },
      );

      if (cheapestFare) {
        const initialSelection: SelectedFlight = {
          route: {
            ...route,
            listTrip: [cheapestTrip],
          },
          trip: cheapestTrip,
          pricing: cheapestFare,
          sessionId: sessionId || '',
          selectedAt: new Date().toISOString(),
        };

        saveSelectedFlight(initialSelection);
      }
    });

    // Trigger refresh
    setFlightRefreshKey(prev => prev + 1);
  };

  /**
   * Handle combo API success response
   * Saves session ID and initial flight selections
   */
  const handleComboSuccess = (response: any) => {
    const sessionId = response?.data?.combo?.flight?.dataSessionId;
    if (sessionId) {
      saveComboSessionId(sessionId);
    }

    // Reset local state
    setSelectedTicketsMap({});
    setSelectedFlight(null);

    // Save initial flights
    const flightData = response?.data?.combo?.flight;
    if (flightData) {
      saveInitialFlights(flightData, sessionId || '');
    }
  };

  /**
   * Save search parameters to session storage
   */
  const handleSaveSearchParams = (params: any) => {
    saveSearchParams(params);
  };

  return {
    selectedTicketsMap,
    setSelectedTicketsMap,
    selectedFlight,
    setSelectedFlight,
    flightRefreshKey,
    setFlightRefreshKey,
    clearSessionForNewSearch,
    handleComboSuccess,
    handleSaveSearchParams,
  };
};
